import { h as __h } from "preact";
import { Component, h } from "preact";

/** @import { enterpriseAPIKeyResponse } from "~/backend/validate.js"; */
import { ApiError, authedApiRequest } from "~/lib/api.js";
import { alert } from "~/util/alert.js";
import html from "~/util/html.js";
import { modal } from "~/util/modal.js";
import { PromiseSignal } from "~/util/promise.js";
import Query from "~/util/query.js";
import Button from "~/component/Button.js";
import Field from "~/component/Field.js";
import Select from "~/component/Select.js";
import adminStyle from "~/admin/lib/style.js";
const OPTIONS = [{
  hours: 0,
  label: "Immediately"
}, {
  hours: 1,
  label: "In 1 hour"
}, {
  hours: 24,
  label: "In 1 day"
}, {
  hours: 3 * 24,
  label: "In 3 days"
}, {
  hours: 7 * 24,
  label: "In 7 days"
}];

/** @typedef {(json: { expires_at: string | null }) => Promise<boolean>} SubmitCallback */

/**
 * @param {{ enterpriseId: string }} props
 * @param {enterpriseAPIKeyResponse} apiKey
 */
export const expireApiKey = async ({
  enterpriseId
}, apiKey) => {
  /** @type {SubmitCallback} */
  const onSubmit = async json => {
    await authedApiRequest("POST", `/v2/enterprises/${enterpriseId}/api-keys/${apiKey.id}/expire`, {
      json
    });
    Query.invalidate([`/v2/enterprises/${enterpriseId}/api-keys`]);
    alert(json.expires_at ? `Scheduled API key '${apiKey.name}' to expire` : `Expired API key '${apiKey.name}'`, {
      type: "success"
    });
    return true;
  };
  return await modal({
    title: `Expire '${apiKey.name}'?`,
    content: (/** @type {(result?: boolean) => void} */resolve) => h(ModalChildren, {
      resolve,
      apiKey,
      onSubmit
    })
  });
};

/**
 * @extends {Component<ModalChildrenProps>}
 *
 * @typedef {object} ModalChildrenProps
 * @property {enterpriseAPIKeyResponse} apiKey
 * @property {(result?: boolean) => void} resolve
 * @property {SubmitCallback} onSubmit
 */
class ModalChildren extends Component {
  formState = new PromiseSignal(/** @param {SubmitEvent & { currentTarget: HTMLFormElement }} ev */
  async ev => {
    const form = new FormData(ev.currentTarget);
    const hours = Number(form.get("expires_in"));
    const expires_at = hours ? new Date(Date.now() + hours * (60 * 60 * 1000)).toISOString() : null;
    this.props.resolve(await this.props.onSubmit({
      expires_at
    }));
  });
  render() {
    const error = this.formState.error && ApiError.from(this.formState.error);

    // An existing expiry can only be moved earlier, so hide options past it.
    const current = this.props.apiKey.expires_at && new Date(this.props.apiKey.expires_at);
    const maxHours = current ? (current.getTime() - Date.now()) / (60 * 60 * 1000) : Infinity;
    const options = OPTIONS.filter(o => o.hours < maxHours);
    return __h("form", {
      "class": adminStyle.modal,
      onSubmit: (/** @type {SubmitEvent & { currentTarget: HTMLFormElement }} */ev) => {
        if (ev.submitter?.getAttribute("formmethod") === "dialog") return;
        ev.preventDefault();
        this.formState.invoke(ev);
      }
    }, __h("p", {}, "Requests made with this key will be rejected once it expires. Expiry can be scheduled up\n\t\t\t\t\tto 7 days out, and once set can only be moved earlier."), __h(Field, {
      "for": "expires_in",
      label: "Expire",
      error: error?.fields.expires_at?.message
    }, __h(Select, {
      id: "expires_in",
      name: "expires_in",
      autofocus: true
    }, options.map(o => __h("option", {
      key: o.hours,
      value: o.hours
    }, o.label)))), __h("p", {
      "class": adminStyle.error
    }, error?.message), __h("div", {
      "class": adminStyle.actions
    }, __h(Button, {
      type: "submit",
      level: "primary",
      kind: "negative",
      loading: this.formState.loading
    }, "Expire key"), __h(Button, {
      type: "submit",
      formmethod: "dialog",
      formnovalidate: true,
      value: "cancel"
    }, "Cancel")));
  }
}